import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import {
  useClearMachineryDirectiveEPlan,
  useGetMachineryDirectiveEPlan,
  useUpdateMachineryDirectiveEPlan,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { useMachineryEPlanUpload } from '../../hooks/useMachineryEPlanUpload'
import { EPlanUploadCard } from './EPlanUploadCard'
import { EPlanFileUploadStatus } from './EPlanFileUploadStatus'
import { EPlanSelectDialog } from './EPlanSelectDialog'
import { EPlanUploadDialog } from './EPlanUploadDialog'

function getEPlanSourceLabel(source: ProductSource): string {
  if (source.classification == null) {
    return source.name
  }
  const classificationLabel = source.classification.replace(/_/g, ' ')
  return `${source.name} (${classificationLabel})`
}

export function EPlanSettingsPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const complianceId = params.machineryDirectiveComplianceId ?? ''
  const { t } = useTranslation('machineryDirectiveCompliance')

  const { data: ePlanData } = useGetMachineryDirectiveEPlan(complianceId)
  const updateEPlan = useUpdateMachineryDirectiveEPlan(complianceId, productId)
  const clearEPlan = useClearMachineryDirectiveEPlan(complianceId, productId)
  const { handleFileSelect, uploadingSourceIds } = useMachineryEPlanUpload({
    productId,
    complianceId,
  })

  const [sourceDialogOpen, setSourceDialogOpen] = useState(false)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [isClearingSelection, setIsClearingSelection] = useState(false)

  const assignedSource = ePlanData?.productSource ?? null
  const showSavedConfirmation = assignedSource != null
  const selectedSourceLabel =
    assignedSource != null ? getEPlanSourceLabel(assignedSource) : ''
  const isSourceUploading =
    assignedSource != null && uploadingSourceIds.has(assignedSource.id)

  const handleSourceConfirm = async (source: ProductSource) => {
    try {
      await updateEPlan.mutateAsync({ productSourceId: source.id })
    } catch {
      toast.error(t('settings.ePlan.linkError'))
    }
  }

  const handleClearSelectedSource = async () => {
    if (assignedSource == null) {
      return
    }

    setIsClearingSelection(true)
    try {
      await clearEPlan.mutateAsync()
    } catch {
      toast.error(t('settings.ePlan.clearSelectedSourceError'))
    } finally {
      setIsClearingSelection(false)
    }
  }

  const handleUploadFileSelect = async (file: File) => {
    await handleFileSelect(file)
  }

  return (
    <div className='space-y-6'>
      <ComplianceSubPageHeader>
        <div className='space-y-0.5'>
          <ComplianceSubPageTitle>
            {t('settings.ePlan.pageTitle')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('settings.ePlan.pageDescription')}
          </p>
        </div>
      </ComplianceSubPageHeader>

      <div className='space-y-4'>
        {showSavedConfirmation && assignedSource != null && (
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.ePlan.selectedSourceLabel')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex flex-wrap items-center justify-between gap-2'>
                <div className='flex min-w-0 items-center gap-2'>
                  <p className='font-medium'>{selectedSourceLabel}</p>
                  <EPlanFileUploadStatus
                    productId={productId}
                    sourceId={assignedSource.id}
                    fileUploadStatus={assignedSource.fileUploadStatus}
                    filePathInBucket={assignedSource.filePathInBucket}
                    ingestionStatus={assignedSource.ingestionStatus}
                    ingestionStage={assignedSource.ingestionStage}
                    ingestionProgress={assignedSource.ingestionProgress}
                    ingestionErrorCode={assignedSource.ingestionErrorCode}
                    ingestionPartialResult={assignedSource.ingestionPartialResult}
                    ingestionMissingPages={assignedSource.ingestionMissingPages}
                    isUploading={isSourceUploading}
                  />
                </div>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  disabled={isClearingSelection}
                  onClick={() => {
                    void handleClearSelectedSource()
                  }}
                >
                  {t('settings.ePlan.clearSelectedSource')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!showSavedConfirmation && (
          <EPlanUploadCard
            onSelectFromSources={() => setSourceDialogOpen(true)}
            onUpload={() => setUploadDialogOpen(true)}
          />
        )}
      </div>

      <EPlanSelectDialog
        productId={productId}
        open={sourceDialogOpen}
        onOpenChange={setSourceDialogOpen}
        selectedSourceId={assignedSource?.id ?? null}
        onConfirm={(source) => {
          void handleSourceConfirm(source)
        }}
      />

      <EPlanUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onFileSelect={handleUploadFileSelect}
      />
    </div>
  )
}
